import { useState, type ReactNode } from 'react';
import { ENTITY_MIME } from './dnd.js';
import { useAppStore, type PanelId, type TabId } from './store.js';

// Body-only wrapper. The panel's title + per-panel chrome
// (channel picker, link toggle, 3-dots actions, close X) lives in
// dockview's tab header via custom-tab.tsx; this component just
// provides the body container + the drop-target wiring for
// drag-an-entity-onto-this-panel.
//
// `title` is kept on the prop interface for back-compat — the tab
// (not this body) renders the visible title, but several callers
// pass it and there's no harm in accepting it.
export interface PanelFrameProps {
  tabId: TabId;
  panelId: PanelId;
  title?: string;
  children: ReactNode;
}

export function PanelFrame({ tabId, panelId, children }: PanelFrameProps) {
  const [dragHover, setDragHover] = useState(false);
  const setPanelEntity = useAppStore((s) => s.actions.setPanelEntity);

  function onDragOver(e: React.DragEvent) {
    if (!e.dataTransfer.types.includes(ENTITY_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'link';
    if (!dragHover) setDragHover(true);
  }
  function onDragLeave() {
    setDragHover(false);
  }
  function onDrop(e: React.DragEvent) {
    setDragHover(false);
    const entityId = e.dataTransfer.getData(ENTITY_MIME);
    if (!entityId) return;
    e.preventDefault();
    setPanelEntity(tabId, panelId, entityId);
  }

  return (
    <section
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'var(--surface)',
        // No shadow here — when a panel is docked flush against
        // neighbours a shadow would project onto them; when a
        // panel is floating, dockview's --dv-floating-box-shadow
        // already supplies the elevation. The drag-an-entity
        // hover outline still wins when active.
        outline: dragHover ? '2px dashed var(--accent)' : 'none',
        outlineOffset: '-2px',
        transition: 'outline-color 80ms',
      }}
      data-panel-id={panelId}
    >
      <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
    </section>
  );
}
